import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Camera, Plus, Check, CheckCheck, Undo2, Trash2, FileDown, Loader2, X, ClipboardCheck, Pencil } from 'lucide-react';
import { markModuleSeen } from '@/lib/moduleBadges';
import ReceptionCelebration from '@/components/ReceptionCelebration';
import { hasReceptionWork } from '@/lib/receptionAccess';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEAMS = ['Eco Bati Construction', 'La Piscine Container', 'Toutes'];

// Reception de chantier : liste des reserves (bilingue), photos avant/apres,
// « fait » par l'ouvrier, « valide » ou « a reprendre » par le manager, PDF.
export default function ReceptionPage() {
  useEffect(() => { markModuleSeen(['reception']); }, []);
  const { language } = useLanguage();
  const { user } = useAuth();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const isManager = user?.role === 'manager';
  const txt = (fr, ru, md, uk) => ({ fr, ru, md, uk }[language] || fr);
  const loc = (item, field) => (item?.[`${field}_i18n`]?.[language]) || item?.[field] || '';

  const [projects, setProjects] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState(null); // item id en cours d'action

  // creation (manager)
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ project_id: '', zone: '', description: '', files: [] });
  const [adding, setAdding] = useState(false);
  const addFileRef = useRef(null);

  // edition (manager)
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({ zone: '', description: '' });

  // action avec commentaire : {item, mode: 'done'|'reject'|'validate'}
  const [action, setAction] = useState(null);
  const [actionComment, setActionComment] = useState('');
  const [actionFiles, setActionFiles] = useState([]);
  const actionFileRef = useRef(null);

  // feu d'artifice apres « c'est fait » : {number, final}
  const [celebrate, setCelebrate] = useState(null);
  const [savingTeam, setSavingTeam] = useState(false);

  // photo plein ecran
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // ajout de photo "apres" direct sur une reserve
  const photoRefs = useRef({});

  const STATUS = {
    open: { label: txt('À faire', 'К исполнению', 'De făcut', 'До виконання'), cls: 'bg-red-100 text-red-700 border-red-200' },
    done: { label: txt('Fait, à valider', 'Сделано, на проверке', 'Făcut, de validat', 'Зроблено, на перевірці'), cls: 'bg-amber-100 text-amber-700 border-amber-200' },
    validated: { label: txt('Validée', 'Принято', 'Validată', 'Прийнято'), cls: 'bg-green-100 text-green-700 border-green-200' },
  };

  const fetchProjects = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reception/summary`);
      // Seuls les chantiers en reception (equipe designee ou reserves) sont proposes,
      // manager compris ; un employe, seulement ceux de sa reception.
      const list = (res.data || []).filter((p) => isManager || hasReceptionWork(p));
      setProjects(list);
      // Un seul chantier en reception (Fitou) : on l'ouvre directement, sans choix.
      if (!projectId && list.length === 1) navigate(`/reception/${list[0].id}`, { replace: true });
    } catch (e) { /* silencieux */ }
  }, [isManager, projectId, navigate]);

  const fetchItems = useCallback(async (pid) => {
    if (!pid) { setData(null); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${pid}/reception`);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || txt('Erreur de chargement', 'Ошибка загрузки', 'Eroare la încărcare', 'Помилка завантаження'));
    } finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  useEffect(() => { fetchItems(projectId); }, [projectId, fetchItems]);
  // Apercu du feu d'artifice (Pavel) : /reception/<chantier>?fete=1
  useEffect(() => {
    const fete = new URLSearchParams(window.location.search).get('fete');
    if (fete) {
      // fete=1 : une reserve cochee ; fete=2 : la derniere reserve du chantier
      setCelebrate({ number: 1, final: fete === '2' });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const selectProject = (pid) => navigate(`/reception/${pid}`);

  const refresh = async () => { await Promise.all([fetchItems(projectId), fetchProjects()]); };

  const replaceItem = (item) => {
    setData((d) => {
      if (!d) return d;
      const items = d.items.map((x) => (x.id === item.id ? item : x));
      const counts = { open: 0, done: 0, validated: 0 };
      items.forEach((x) => { counts[x.status] = (counts[x.status] || 0) + 1; });
      return { ...d, items, counts };
    });
    fetchProjects();
  };

  // ---- creation
  const handleAdd = async () => {
    const pid = projectId || addForm.project_id || (projects.length === 1 ? projects[0].id : '');
    if (!pid) { toast.error(txt('Choisissez le chantier', 'Выберите объект', 'Alegeți șantierul', "Оберіть об'єкт")); return; }
    // Une seule case suffit : si la remarque est ecrite dans « Zone », elle devient la description.
    const description = addForm.description.trim() || addForm.zone.trim();
    const zone = addForm.description.trim() ? addForm.zone.trim() : '';
    if (!description) { toast.error(txt('Écrivez la réserve', 'Напишите замечание', 'Scrieți rezerva', 'Напишіть зауваження')); return; }
    setAdding(true);
    try {
      const fd = new FormData();
      fd.append('description', description);
      fd.append('zone', zone);
      addForm.files.forEach((f) => fd.append('photos', f));
      await axios.post(`${API}/projects/${pid}/reception`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      toast.success(txt('Réserve ajoutée (traduite automatiquement)', 'Замечание добавлено', 'Rezervă adăugată', 'Зауваження додано'));
      setShowAdd(false);
      setAddForm({ project_id: '', zone: '', description: '', files: [] });
      if (pid !== projectId) navigate(`/reception/${pid}`); else await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || txt('Erreur', 'Ошибка', 'Eroare', 'Помилка'));
    } finally { setAdding(false); }
  };

  // ---- edition
  const handleEdit = async () => {
    if (!editItem) return;
    setBusy(editItem.id);
    try {
      const res = await axios.put(`${API}/projects/${projectId}/reception/${editItem.id}`, { zone: editForm.zone, description: editForm.description }, { timeout: 120000 });
      replaceItem(res.data);
      setEditItem(null);
      toast.success(txt('Réserve modifiée', 'Замечание изменено', 'Rezervă modificată', 'Зауваження змінено'));
    } catch (e) {
      toast.error(e.response?.data?.detail || txt('Erreur', 'Ошибка', 'Eroare', 'Помилка'));
    } finally { setBusy(null); }
  };

  // ---- photos "apres" (ou "avant" pour le manager)
  const uploadPhotos = async (item, files, kind) => {
    if (!files?.length) return null;
    const fd = new FormData();
    fd.append('kind', kind);
    Array.from(files).forEach((f) => fd.append('photos', f));
    const res = await axios.post(`${API}/projects/${projectId}/reception/${item.id}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
    return res.data;
  };

  const handleQuickPhotos = async (item, e, kind) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setBusy(item.id);
    try {
      const updated = await uploadPhotos(item, files, kind);
      if (updated) replaceItem(updated);
      toast.success(txt('Photo ajoutée', 'Фото добавлено', 'Foto adăugată', 'Фото додано'));
    } catch (err) {
      toast.error(err.response?.data?.detail || txt('Erreur', 'Ошибка', 'Eroare', 'Помилка'));
    } finally { setBusy(null); }
  };

  // ---- actions de cycle de vie
  const openAction = (item, mode) => { setAction({ item, mode }); setActionComment(''); setActionFiles([]); };

  const runAction = async () => {
    if (!action) return;
    const { item, mode } = action;
    if (mode === 'reject' && !actionComment.trim()) {
      toast.error(txt('Dites ce qui ne va pas', 'Укажите, что не так', 'Spuneți ce nu este în regulă', 'Вкажіть, що не так'));
      return;
    }
    setBusy(item.id);
    try {
      if (actionFiles.length) await uploadPhotos(item, actionFiles, 'after');
      const res = await axios.post(`${API}/projects/${projectId}/reception/${item.id}/${mode}`, { comment: actionComment }, { timeout: 120000 });
      replaceItem(res.data);
      setAction(null);
      if (mode === 'done') {
        setCelebrate({ number: item.number, final: (res.data.remaining_open ?? 1) === 0 });
        return;
      }
      toast.success({
        done: txt('Marquée comme faite', 'Отмечено как сделано', 'Marcată ca făcută', 'Позначено як зроблено'),
        validate: txt('Réserve validée', 'Замечание принято', 'Rezervă validată', 'Зауваження прийнято'),
        reject: txt('Réserve renvoyée à l\'équipe', 'Замечание возвращено', 'Rezervă retrimisă', 'Зауваження повернуто'),
      }[mode]);
    } catch (e) {
      toast.error(e.response?.data?.detail || txt('Erreur', 'Ошибка', 'Eroare', 'Помилка'));
    } finally { setBusy(null); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(txt('Supprimer cette réserve ?', 'Удалить это замечание?', 'Ștergeți această rezervă?', 'Видалити це зауваження?'))) return;
    setBusy(item.id);
    try {
      await axios.delete(`${API}/projects/${projectId}/reception/${item.id}`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || txt('Erreur', 'Ошибка', 'Eroare', 'Помилка'));
    } finally { setBusy(null); }
  };

  const openPreview = async (item, photo) => {
    setPreview({ src: photo.thumb, kind: photo.kind, user_name: photo.user_name });
    setPreviewLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${projectId}/reception/${item.id}/photos/${photo.id}`);
      setPreview((p) => (p ? { ...p, src: res.data.data || p.src } : p));
    } catch (e) { /* on garde la vignette */ } finally { setPreviewLoading(false); }
  };

  const setTeam = async (company) => {
    setSavingTeam(true);
    try {
      const res = await axios.put(`${API}/projects/${projectId}/reception/team`, { company });
      setData((d) => (d ? { ...d, project: res.data } : d));
      toast.success(txt('Équipe de réception enregistrée', 'Команда сохранена', 'Echipă salvată', 'Команду збережено'));
    } catch (e) {
      toast.error(e.response?.data?.detail || txt('Erreur', 'Ошибка', 'Eroare', 'Помилка'));
    } finally { setSavingTeam(false); }
  };

  const teamLabel = (c) => ({ 'Eco Bati Construction': 'Éco Bâti (EBC)', 'La Piscine Container': 'La Piscine Container (LPC)', Toutes: txt('Les deux équipes', 'Обе команды', 'Ambele echipe', 'Обидві команди') }[c] || c);

  const downloadPdf = async (lang) => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/reception/pdf`, { params: { lang }, responseType: 'blob', timeout: 120000 });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Reception ${data?.project?.name || ''} - ${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error(txt('PDF impossible', 'Не удалось создать PDF', 'PDF imposibil', 'PDF неможливий'));
    }
  };

  const items = (data?.items || []).filter((it) => filter === 'all' || it.status === filter);
  const counts = data?.counts || { open: 0, done: 0, validated: 0 };
  const total = data?.total || 0;
  const pct = total ? Math.round((counts.validated / total) * 100) : 0;
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString(language === 'ru' ? 'ru-RU' : language === 'uk' ? 'uk-UA' : language === 'md' ? 'ro-RO' : 'fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');

  return (
    <div className="space-y-4 pb-24">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ClipboardCheck className="w-7 h-7 text-teal-600" />
          {txt('Réception', 'Приёмка', 'Recepție', 'Приймання')}
        </h1>
        <div className="flex gap-2">
          {projectId && (
            <Button variant="outline" size="sm" onClick={() => downloadPdf(language === 'fr' ? 'ru' : language)} title="PDF">
              <FileDown className="w-4 h-4 mr-1" /> PDF
            </Button>
          )}
          {isManager && (
            <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={() => setShowAdd(true)} data-testid="reception-add">
              <Plus className="w-4 h-4 mr-1" /> {txt('Réserve', 'Замечание', 'Rezervă', 'Зауваження')}
            </Button>
          )}
        </div>
      </div>

      {/* Selecteur de chantier */}
      <div className="card-base p-3">
        <Label className="text-xs text-gray-500">{txt('Chantier', 'Объект', 'Șantier', "Об'єкт")}</Label>
        <Select value={projectId || ''} onValueChange={selectProject}>
          <SelectTrigger className="mt-1"><SelectValue placeholder={txt('Choisir un chantier', 'Выберите объект', 'Alegeți șantierul', "Оберіть об'єкт")} /></SelectTrigger>
          <SelectContent>
            {projects.map((p) => {
              const r = p.reception || {};
              const n = (r.open || 0) + (r.done || 0) + (r.validated || 0);
              return (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}{n ? ` · ${r.validated || 0}/${n}` : ''}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {!projectId && (
        <div className="card-base p-6 text-center text-gray-500 text-sm">
          {txt('Choisissez un chantier pour voir ses réserves.', 'Выберите объект, чтобы увидеть замечания.', 'Alegeți un șantier pentru a vedea rezervele.', "Оберіть об'єкт, щоб побачити зауваження.")}
        </div>
      )}

      {projectId && data && (
        <>
          {/* Compteurs + avancement */}
          <div className="card-base p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold">{data.project?.name}</span>
              <span className="text-sm text-gray-500">{counts.validated}/{total} {txt('validées', 'принято', 'validate', 'прийнято')}</span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-green-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
            {isManager ? (
              <div className="mt-3 flex items-center gap-2">
                <Label className="text-xs text-gray-500 whitespace-nowrap">{txt('Équipe chargée de la réception', 'Команда приёмки', 'Echipa de recepție', 'Команда приймання')}</Label>
                <Select value={data.project?.reception_company || data.project?.company || 'Toutes'} onValueChange={setTeam} disabled={savingTeam}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TEAMS.map((c) => <SelectItem key={c} value={c}>{teamLabel(c)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="mt-2 text-xs text-gray-500">{txt('Équipe', 'Команда', 'Echipă', 'Команда')} : {teamLabel(data.project?.reception_company || data.project?.company || 'Toutes')}</div>
            )}
            <div className="grid grid-cols-4 gap-1 mt-3 text-xs">
              {[['all', txt('Toutes', 'Все', 'Toate', 'Усі'), total, 'bg-gray-100 text-gray-700'],
                ['open', STATUS.open.label, counts.open, 'bg-red-50 text-red-700'],
                ['done', STATUS.done.label, counts.done, 'bg-amber-50 text-amber-700'],
                ['validated', STATUS.validated.label, counts.validated, 'bg-green-50 text-green-700']].map(([key, label, n, cls]) => (
                <button key={key} onClick={() => setFilter(key)}
                  className={`rounded-lg px-1 py-2 text-center border ${cls} ${filter === key ? 'ring-2 ring-teal-500 font-semibold' : 'border-transparent'}`}>
                  <div className="text-lg leading-none font-bold">{n}</div>
                  <div className="leading-tight mt-1 line-clamp-2">{label}</div>
                </button>
              ))}
            </div>
          </div>

          {loading && <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>}

          {!loading && items.length === 0 && (
            <div className="card-base p-6 text-center text-gray-500 text-sm">
              {total === 0
                ? txt('Aucune réserve pour ce chantier.', 'Замечаний по этому объекту нет.', 'Nicio rezervă pentru acest șantier.', "Зауважень по цьому об'єкту немає.")
                : txt('Rien dans ce filtre.', 'В этом фильтре ничего нет.', 'Nimic în acest filtru.', 'У цьому фільтрі нічого немає.')}
              {isManager && total === 0 && (
                <div className="mt-3">
                  <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={() => setShowAdd(true)}>
                    <Plus className="w-4 h-4 mr-1" /> {txt('Ajouter la première réserve', 'Добавить первое замечание', 'Adaugă prima rezervă', 'Додати перше зауваження')}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Liste des reserves */}
          {items.map((it) => {
            const before = (it.photos || []).filter((p) => p.kind === 'before');
            const after = (it.photos || []).filter((p) => p.kind === 'after');
            const st = STATUS[it.status] || STATUS.open;
            const isBusy = busy === it.id;
            return (
              <div key={it.id} className={`card-base p-4 space-y-3 ${it.status === 'validated' ? 'opacity-80' : ''}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-xs text-gray-500">n° {it.number}{loc(it, 'zone') ? ` · ${loc(it, 'zone')}` : ''}</div>
                    <div className="font-medium leading-snug">{loc(it, 'description')}</div>
                    {language !== 'fr' && it.description && it.description !== loc(it, 'description') && (
                      <div className="text-xs text-gray-400 mt-0.5">{it.description}</div>
                    )}
                  </div>
                  <span className={`shrink-0 text-xs px-2 py-1 rounded-full border ${st.cls}`}>{st.label}</span>
                </div>

                {(before.length > 0 || after.length > 0) && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">{txt('Avant', 'До', 'Înainte', 'До')}</div>
                      <div className="flex gap-1 flex-wrap">
                        {before.map((p) => (
                          <button key={p.id} onClick={() => openPreview(it, p)} className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 border">
                            <img src={p.thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
                          </button>
                        ))}
                        {before.length === 0 && <span className="text-xs text-gray-300">,</span>}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">{txt('Après', 'После', 'După', 'Після')}</div>
                      <div className="flex gap-1 flex-wrap">
                        {after.map((p) => (
                          <button key={p.id} onClick={() => openPreview(it, p)} className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 border border-green-200">
                            <img src={p.thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
                          </button>
                        ))}
                        {it.status !== 'validated' && (
                          <button onClick={() => photoRefs.current[it.id]?.click()} disabled={isBusy}
                            className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-teal-500 hover:text-teal-600">
                            {isBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <input type="file" accept="image/*" capture="environment" multiple className="hidden"
                  ref={(el) => { photoRefs.current[it.id] = el; }} onChange={(e) => handleQuickPhotos(it, e, 'after')} />

                {it.done_at && (
                  <div className="text-xs text-gray-500">
                    {txt('Fait par', 'Выполнил', 'Făcut de', 'Виконав')} <b>{it.done_by_name}</b> · {fmt(it.done_at)}
                    {it.done_comment && <div className="mt-0.5 text-gray-700 italic">« {loc(it, 'done_comment')} »</div>}
                  </div>
                )}
                {it.manager_comment && (
                  <div className={`text-xs rounded-lg px-2 py-1.5 ${it.status === 'open' ? 'bg-red-50 text-red-800' : 'bg-gray-50 text-gray-700'}`}>
                    <b>{it.status === 'open' ? txt('À reprendre : ', 'Переделать: ', 'De refăcut: ', 'Переробити: ') : ''}</b>{loc(it, 'manager_comment')}
                  </div>
                )}
                {it.validated_at && (
                  <div className="text-xs text-green-700 flex items-center gap-1"><CheckCheck className="w-3.5 h-3.5" /> {txt('Validée par', 'Принял', 'Validată de', 'Прийняв')} {it.validated_by_name} · {fmt(it.validated_at)}</div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-1">
                  {it.status === 'open' && (
                    <Button size="sm" className="bg-teal-600 hover:bg-teal-700 flex-1" disabled={isBusy} onClick={() => openAction(it, 'done')}>
                      <Check className="w-4 h-4 mr-1" /> {txt("C'est fait", 'Сделано', 'E făcut', 'Зроблено')}
                    </Button>
                  )}
                  {(before.length === 0 && after.length === 0 && it.status !== 'validated') && (
                    <Button size="sm" variant="outline" disabled={isBusy} onClick={() => photoRefs.current[it.id]?.click()}>
                      <Camera className="w-4 h-4 mr-1" /> {txt('Photo', 'Фото', 'Foto', 'Фото')}
                    </Button>
                  )}
                  {isManager && it.status === 'done' && (
                    <>
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 flex-1" disabled={isBusy} onClick={() => openAction(it, 'validate')}>
                        <CheckCheck className="w-4 h-4 mr-1" /> {txt('Valider', 'Принять', 'Validează', 'Прийняти')}
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-700 border-red-200" disabled={isBusy} onClick={() => openAction(it, 'reject')}>
                        <Undo2 className="w-4 h-4 mr-1" /> {txt('À reprendre', 'Переделать', 'De refăcut', 'Переробити')}
                      </Button>
                    </>
                  )}
                  {isManager && it.status === 'open' && (
                    <Button size="sm" variant="outline" className="text-green-700 border-green-200" disabled={isBusy} onClick={() => openAction(it, 'validate')} title={txt('Valider directement', 'Принять сразу', 'Validează direct', 'Прийняти одразу')}>
                      <CheckCheck className="w-4 h-4" />
                    </Button>
                  )}
                  {isManager && it.status === 'validated' && (
                    <Button size="sm" variant="outline" className="text-red-700 border-red-200" disabled={isBusy} onClick={() => openAction(it, 'reject')}>
                      <Undo2 className="w-4 h-4 mr-1" /> {txt('Rouvrir', 'Открыть снова', 'Redeschide', 'Відкрити знову')}
                    </Button>
                  )}
                  {isManager && (
                    <>
                      <Button size="sm" variant="ghost" disabled={isBusy} onClick={() => { setEditItem(it); setEditForm({ zone: it.zone || '', description: it.description || '' }); }}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-gray-400 hover:text-red-600" disabled={isBusy} onClick={() => handleDelete(it)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* Dialog : nouvelle reserve */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{txt('Nouvelle réserve', 'Новое замечание', 'Rezervă nouă', 'Нове зауваження')}{data?.project?.name && projectId ? ` · ${data.project.name}` : ''}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {!projectId && (
              <div>
                <Label>{txt('Chantier', 'Объект', 'Șantier', "Об'єкт")}</Label>
                <Select value={addForm.project_id} onValueChange={(v) => setAddForm({ ...addForm, project_id: v })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder={txt('Choisir un chantier', 'Выберите объект', 'Alegeți șantierul', "Оберіть об'єкт")} /></SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>{txt('Réserve : ce qui doit être fait', 'Замечание: что нужно сделать', 'Rezervă: ce trebuie făcut', 'Зауваження: що потрібно зробити')}</Label>
              <Textarea rows={3} value={addForm.description} onChange={(e) => setAddForm({ ...addForm, description: e.target.value })} placeholder={txt('Poser le moteur, reprendre le joint…', 'Установить мотор, переделать шов…', 'Montați motorul, refaceți rostul…', 'Встановити мотор, переробити шов…')} />
              <p className="text-xs text-gray-500 mt-1">{txt('Traduite automatiquement en russe, moldave et ukrainien.', 'Переводится автоматически.', 'Tradusă automat.', 'Перекладається автоматично.')}</p>
            </div>
            <div>
              <Label>{txt('Zone / pièce (facultatif)', 'Зона / помещение (необязательно)', 'Zonă / cameră (opțional)', "Зона / кімната (необов'язково)")}</Label>
              <Input value={addForm.zone} onChange={(e) => setAddForm({ ...addForm, zone: e.target.value })} placeholder={txt('Salle de bain, terrasse, cuisine…', 'Ванная, терраса, кухня…', 'Baie, terasă, bucătărie…', 'Ванна, тераса, кухня…')} />
            </div>
            <div>
              <Label>{txt('Photos (avant)', 'Фото (до)', 'Fotografii (înainte)', 'Фото (до)')}</Label>
              <input ref={addFileRef} type="file" accept="image/*" capture="environment" multiple className="hidden"
                onChange={(e) => setAddForm({ ...addForm, files: [...addForm.files, ...Array.from(e.target.files || [])] })} />
              <div className="flex gap-2 flex-wrap mt-1">
                {addForm.files.map((f, i) => (
                  <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border">
                    <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                    <button className="absolute top-0 right-0 bg-black/60 text-white rounded-bl p-0.5" onClick={() => setAddForm({ ...addForm, files: addForm.files.filter((_, j) => j !== i) })}><X className="w-3 h-3" /></button>
                  </div>
                ))}
                <button onClick={() => addFileRef.current?.click()} className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-teal-500 hover:text-teal-600">
                  <Camera className="w-5 h-5" />
                </button>
              </div>
            </div>
            <Button className="w-full bg-teal-600 hover:bg-teal-700" disabled={adding} onClick={handleAdd}>
              {adding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {txt('Ajouter la réserve', 'Добавить', 'Adaugă rezerva', 'Додати')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog : edition */}
      <Dialog open={!!editItem} onOpenChange={(o) => { if (!o) setEditItem(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{txt('Modifier la réserve', 'Изменить замечание', 'Modifică rezerva', 'Змінити зауваження')} n° {editItem?.number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>{txt('Zone / pièce', 'Зона', 'Zonă', 'Зона')}</Label><Input value={editForm.zone} onChange={(e) => setEditForm({ ...editForm, zone: e.target.value })} /></div>
            <div><Label>{txt('Description', 'Описание', 'Descriere', 'Опис')}</Label><Textarea rows={3} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} /></div>
            <Button className="w-full" disabled={busy === editItem?.id} onClick={handleEdit}>{txt('Enregistrer', 'Сохранить', 'Salvează', 'Зберегти')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog : action (fait / valider / reprendre) */}
      <Dialog open={!!action} onOpenChange={(o) => { if (!o) setAction(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {action?.mode === 'done' && txt('Réserve faite', 'Замечание выполнено', 'Rezervă făcută', 'Зауваження виконано')}
              {action?.mode === 'validate' && txt('Valider la réserve', 'Принять замечание', 'Validează rezerva', 'Прийняти зауваження')}
              {action?.mode === 'reject' && txt('Renvoyer à reprendre', 'Вернуть на доработку', 'Trimite de refăcut', 'Повернути на доопрацювання')}
              {action ? ` · n° ${action.item.number}` : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {action && <p className="text-sm text-gray-600">{loc(action.item, 'description')}</p>}
            {action?.mode === 'done' && (
              <div>
                <Label>{txt('Photo du travail fait', 'Фото выполненной работы', 'Foto cu lucrarea făcută', 'Фото виконаної роботи')}</Label>
                <input ref={actionFileRef} type="file" accept="image/*" capture="environment" multiple className="hidden"
                  onChange={(e) => setActionFiles([...actionFiles, ...Array.from(e.target.files || [])])} />
                <div className="flex gap-2 flex-wrap mt-1">
                  {actionFiles.map((f, i) => (
                    <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border">
                      <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                      <button className="absolute top-0 right-0 bg-black/60 text-white rounded-bl p-0.5" onClick={() => setActionFiles(actionFiles.filter((_, j) => j !== i))}><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                  <button onClick={() => actionFileRef.current?.click()} className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-teal-500 hover:text-teal-600">
                    <Camera className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
            <div>
              <Label>{txt('Commentaire', 'Комментарий', 'Comentariu', 'Коментар')}{action?.mode === 'reject' ? ' *' : ` (${txt('facultatif', 'необязательно', 'opțional', "необов'язково")})`}</Label>
              <Textarea rows={2} value={actionComment} onChange={(e) => setActionComment(e.target.value)}
                placeholder={action?.mode === 'reject' ? txt('Ce qui ne va pas…', 'Что не так…', 'Ce nu este în regulă…', 'Що не так…') : txt('Écrivez dans votre langue, c\'est traduit.', 'Пишите на своём языке, будет переведено.', 'Scrieți în limba dvs., se traduce.', 'Пишіть своєю мовою, буде перекладено.')} />
            </div>
            <Button className={`w-full ${action?.mode === 'reject' ? 'bg-red-600 hover:bg-red-700' : action?.mode === 'validate' ? 'bg-green-600 hover:bg-green-700' : 'bg-teal-600 hover:bg-teal-700'}`}
              disabled={busy === action?.item?.id} onClick={runAction}>
              {busy === action?.item?.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              {txt('Confirmer', 'Подтвердить', 'Confirmă', 'Підтвердити')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {celebrate && (
        <ReceptionCelebration language={language} name={user?.name?.split(' ')[0]} number={celebrate.number} final={celebrate.final} onClose={() => setCelebrate(null)} />
      )}

      {/* Photo plein ecran */}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="sm:max-w-3xl p-0 overflow-hidden">
          {preview && (
            <div className="relative bg-black">
              {previewLoading && <div className="absolute inset-0 flex items-center justify-center z-10"><Loader2 className="w-8 h-8 text-white animate-spin" /></div>}
              <img src={preview.src} alt="" className="w-full max-h-[80vh] object-contain" />
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs px-3 py-2 flex justify-between">
                <span>{preview.kind === 'after' ? txt('Après', 'После', 'După', 'Після') : txt('Avant', 'До', 'Înainte', 'До')}</span>
                <span>{preview.user_name}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
